import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  HomeIcon,
  UsersIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  UserCircleIcon,
  ChartBarIcon,
  FolderIcon,
  BeakerIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftOnRectangleIcon,
  TableCellsIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface MenuItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string | number;
  children?: MenuItem[];
  roles?: string[];
}

interface SidebarProps {
  open: boolean;
  mobileOpen: boolean;
  onClose: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ open, mobileOpen, onClose }) => {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const menuItems: MenuItem[] = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: 'Patients',
      path: '/patients',
      icon: UsersIcon,
      badge: '12',
      children: [
        { name: 'All Patients', path: '/patients', icon: UsersIcon },
        { name: 'Pending', path: '/patients/pending', icon: ClipboardDocumentListIcon },
        { name: 'Completed', path: '/patients/completed', icon: DocumentTextIcon },
      ],
    },
    {
      name: 'Doctors',
      path: '/doctors',
      icon: UserCircleIcon,
      roles: ['admin'],
      children: [
        { name: 'Manage', path: '/admin/doctors', icon: UserCircleIcon },
        { name: 'Payments', path: '/doctors', icon: UserCircleIcon },
      ],
    },
    {
      name: 'Templates',
      path: '/templates',
      icon: FolderIcon,
      roles: ['admin'],
    },
    {
      name: 'Analytics',
      path: '/analytics',
      icon: ChartBarIcon,
      children: [
        { name: 'Overview', path: '/analytics', icon: ChartBarIcon },
        { name: 'Reports', path: '/analytics/reports', icon: DocumentTextIcon },
      ],
    },
    {
      name: 'Sheets Sync',
      path: '/admin/sheets-sync',
      icon: TableCellsIcon,
      roles: ['admin'],
      children: [
        { name: 'Sync', path: '/admin/sheets-sync', icon: ArrowPathIcon },
        { name: 'Settings', path: '/admin/sheets-settings', icon: Cog6ToothIcon },
      ],
    },
    {
      name: 'Settings',
      path: '/settings',
      icon: Cog6ToothIcon,
    },
  ];

  // Filter menu items by user role
  const filteredMenuItems = menuItems.filter((item) => {
    if (!item.roles) return true;
    return item.roles.some((role) => user?.groups?.includes(role));
  });

  // Add dev-only test page
  if (process.env.NODE_ENV === 'development') {
    filteredMenuItems.push({
      name: 'Auth Test',
      path: '/auth-test',
      icon: BeakerIcon,
    });
  }

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((m) => m !== name) : [...prev, name]
    );
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const SidebarContent = () => (
    <>
      {/* Logo & Brand */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <Link to="/dashboard" className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          {open && (
            <div className="flex flex-col">
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                CertifyMyHealth
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Medical Portal
              </span>
            </div>
          )}
        </Link>
      </div>

      {/* User Profile */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-semibold shadow-lg">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          {open && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {user?.email || ''}
              </p>
              {user?.groups && user.groups.length > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mt-1">
                  {user.groups[0]}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-4 space-y-1">
        {filteredMenuItems.map((item) => (
          <div key={item.name}>
            {item.children ? (
              // Menu with children
              <div>
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon className="w-5 h-5" />
                    {open && <span className="font-medium">{item.name}</span>}
                  </div>
                  {open && (
                    <div className="flex items-center space-x-2">
                      {item.badge && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                          {item.badge}
                        </span>
                      )}
                      {expandedMenus.includes(item.name) ? (
                        <ChevronDownIcon className="w-4 h-4" />
                      ) : (
                        <ChevronRightIcon className="w-4 h-4" />
                      )}
                    </div>
                  )}
                </button>

                {/* Submenu */}
                {open && expandedMenus.includes(item.name) && (
                  <div className="ml-4 mt-1 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        onClick={onClose}
                        className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-200 ${
                          isActive(child.path)
                            ? 'bg-blue-50 dark:bg-gray-800 text-blue-600 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                      >
                        <child.icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{child.name}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              // Simple menu item
              <Link
                to={item.path}
                onClick={onClose}
                className={`flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <item.icon className="w-5 h-5" />
                  {open && <span className="font-medium">{item.name}</span>}
                </div>
                {open && item.badge && (
                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white">
                    {item.badge}
                  </span>
                )}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Sign Out Button */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={handleSignOut}
          className="w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200"
        >
          <ArrowLeftOnRectangleIcon className="w-5 h-5" />
          {open && <span className="font-medium">Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed top-0 left-0 h-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border-r border-gray-200 dark:border-gray-700 transition-all duration-300 ease-in-out z-30 shadow-xl ${
          open ? 'w-64' : 'w-20'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out z-30 shadow-2xl ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  );
};